import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from './TranslationContext.tsx';
import { localizeAchievementName } from '../utils/achievements.ts';
import './AchievementNotification.css';

export interface EarnedAchievement {
  id: number;
  name: string;
  points: number;
  key?: string | null;
}

interface NotificationItem extends EarnedAchievement {
  toastId: number;
}

interface AchievementNotificationContextType {
  showAchievements: (achievements: EarnedAchievement[]) => void;
}

const AchievementNotificationContext = createContext<AchievementNotificationContextType | null>(null);

let nextToastId = 0;

export function AchievementNotificationProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const showAchievements = useCallback((achievements: EarnedAchievement[]) => {
    if (!achievements?.length) return;
    const items: NotificationItem[] = achievements.map((a) => ({
      ...a,
      toastId: ++nextToastId,
    }));
    setNotifications((prev) => [...prev, ...items]);
    items.forEach((item) => {
      setTimeout(() => {
        setNotifications((p) => p.filter((n) => n.toastId !== item.toastId));
      }, 4500);
    });
  }, []);

  const dismiss = useCallback((toastId: number) => {
    setNotifications((p) => p.filter((n) => n.toastId !== toastId));
  }, []);

  return (
    <AchievementNotificationContext.Provider value={{ showAchievements }}>
      {children}
      <div className="achievement-notification-container" aria-live="polite">
        {notifications.map((n) => {
          const name = localizeAchievementName(t, n);
          return (
            <div
              key={n.toastId}
              className="achievement-notification"
              role="status"
            >
              <div className="achievement-notification-header">
                <span className="achievement-notification-badge">🏆</span>
                <span className="achievement-notification-title">
                  {t('achievements.toastUnlocked').replace('{{name}}', name)}
                </span>
                <button
                  type="button"
                  className="achievement-notification-close"
                  onClick={() => dismiss(n.toastId)}
                  aria-label={t('achievements.toastCloseAria')}
                >
                  ×
                </button>
              </div>
              <div className="achievement-notification-points">
                {t('achievements.toastPoints').replace('{{points}}', String(n.points))}
              </div>
            </div>
          );
        })}
      </div>
    </AchievementNotificationContext.Provider>
  );
}

export function useAchievementNotifications() {
  const ctx = useContext(AchievementNotificationContext);
  return ctx;
}
